import type { JsonApiDocument, JsonApiResource, JsonApiResourceIdentifier } from './types';

export interface Denormalized {
  type: string;
  id: string;
  [key: string]: unknown;
}

/**
 * Walk a JSON:API document and produce a denormalized JS object where
 * relationships are inlined from `included`. Cycle-safe via resolved-map.
 *
 * Contratto v1.7.0: attributi e relationships arrivano gia' in camelCase dal BE,
 * nessuna conversione di casing necessaria. Solo `type` e route restano kebab-case.
 */
export function denormalizeOne(
  resource: JsonApiResource,
  includedMap: Map<string, JsonApiResource>,
  resolved: Map<string, Denormalized> = new Map()
): Denormalized {
  const key = `${resource.type}:${resource.id}`;
  const cached = resolved.get(key);
  if (cached) return cached;

  // Some resources expose an attribute named `type` (e.g. project-tasks has the
  // WBS kind 'feature'/'activity'/'step'/'task'). It would collide with the
  // top-level JSON:API `type` (= 'project-tasks'). Rename to `taskType` to
  // disambiguate. Other naming collisions can be added here if they emerge.
  const rawAttrs = resource.attributes ?? {};
  const attrs: Record<string, unknown> = { ...rawAttrs };
  if ('type' in attrs && resource.type === 'project-tasks') {
    attrs.taskType = attrs.type;
    delete attrs.type;
  }

  const out: Denormalized = {
    type: resource.type,
    id: resource.id,
    ...attrs,
  };
  resolved.set(key, out);

  if (resource.relationships) {
    for (const [name, rel] of Object.entries(resource.relationships)) {
      if (!rel.data) {
        out[name] = null;
        continue;
      }
      if (Array.isArray(rel.data)) {
        out[name] = rel.data.map((id) => resolveIdentifier(id, includedMap, resolved));
      } else {
        out[name] = resolveIdentifier(rel.data, includedMap, resolved);
      }
    }
  }

  return out;
}

function resolveIdentifier(
  id: JsonApiResourceIdentifier,
  includedMap: Map<string, JsonApiResource>,
  resolved: Map<string, Denormalized>
): Denormalized | JsonApiResourceIdentifier {
  const full = includedMap.get(`${id.type}:${id.id}`);
  if (!full) return id;
  return denormalizeOne(full, includedMap, resolved);
}

export function denormalize<T extends Denormalized = Denormalized>(doc: JsonApiDocument): T | T[] | null {
  if (!doc.data) return null;
  const includedMap = new Map<string, JsonApiResource>();
  for (const inc of doc.included ?? []) {
    includedMap.set(`${inc.type}:${inc.id}`, inc);
  }
  const resolved = new Map<string, Denormalized>();
  if (Array.isArray(doc.data)) {
    return doc.data.map((d) => denormalizeOne(d, includedMap, resolved)) as T[];
  }
  return denormalizeOne(doc.data, includedMap, resolved) as T;
}
